import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from '@usas/ui-web/wrappers/ReactRouterDom';

import validate from './OrgUnitAdminRosterEntriesIndividualOverrideValidation';

import NavLinks from '../../../meet/NavLinks';
import EntryNavLinks from '../../EntryNavLinks';

import useEventMeetEventData from '../../../../state/eventMeetEvent/UseEventMeetEventData';
import useOmeMeetData from '../../../../state/omeMeet/UseOmeMeetData';
import useOmeMeetOrgUnitAthleteEntryIndividualRosterEntryData from '../../../../state/omeMeetOrgUnitAthleteEntryIndividualRosterEntry/UseOmeMeetOrgUnitAthleteEntryIndividualRosterEntryData';
import useOrgUnitAthleteEntryIndividualRosterEntryData from '../../../../state/orgUnitAthleteEntry/UseOrgUnitAthleteEntryIndividualRosterEntryData';
import useOmeMeetOrgUnitAthleteEntryData from '../../../../state/omeMeetOrgUnitAthleteEntry/UseOmeMeetOrgUnitAthleteEntryData';
import useOmeMeetOrgUnitEntryData from '../../../../state/omeMeetOrgUnitEntry/UseOmeMeetOrgUnitEntryData';
import useOmeMeetOrgUnitEntryInfoData from '../../../../state/omeMeetOrgUnitEntryInfo/UseOmeMeetOrgUnitEntryInfoData';

import useLeftNavModalData from '@usas/ui-web/state/leftNavModal/UseLeftNavModalData';

import Constants from '@usas/ui-web/utils/Constants';
import UseForm from '@usas/ui-web/utils/UseForm';
import { formatDate } from '@usas/ui-web/utils/DateFunctions';
import { convertTimeToMilliseconds, formatTimeForDisplay, formatTimeForFilterObject } from '@usas/ui-web/utils/TimesUtils';

const INITIAL_FORM_STATE = {
  isEdit: false,
  timeCuts: [],
  swimTime: '',
  swimTimeValue: '',
  swimDate: Constants.BLANK_DATE_STRING,
  eventId: Constants.DEFAULT_ID,
  eventName: '',
  courseId: Constants.DEFAULT_ID,
  courseName: '',
  meetName: '',
  hasTimeStandards: false,
  hasQualifyingWindow: false,
  qualifyingStartDate: Constants.BLANK_DATE_STRING,
  qualifyingEndDate: Constants.BLANK_DATE_STRING
};

const useOrgUnitAdminRosterEntriesIndividualOverride = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { eventMeetEventState } = useEventMeetEventData();
  const { omeMeetState } = useOmeMeetData()
  const { omeMeetOrgUnitEntryState } = useOmeMeetOrgUnitEntryData();
  const { omeMeetOrgUnitEntryInfoState } = useOmeMeetOrgUnitEntryInfoData();
  const { omeMeetOrgUnitAthleteEntryIndividualRosterEntryState, getOrgUnitAthleteEntryIndividualRosterEntryByOmeMeetOrgUnitEntryId } = useOmeMeetOrgUnitAthleteEntryIndividualRosterEntryData();
  const { orgUnitAthleteEntryIndividualRosterEntryState, putOrgUnitAthleteEntryIndividualRosterEntry } = useOrgUnitAthleteEntryIndividualRosterEntryData();
  const { omeMeetOrgUnitAthleteEntryState, getOrgUnitAthleteEntryRoster } = useOmeMeetOrgUnitAthleteEntryData();
  const { leftNavModalState, setLeftNavModalState } = useLeftNavModalData();
  const [state, setState] = useState({ athlete: {}, event: {}, tryGet: false, tryGetLocationState: false, tryRedirect: false, courseOptions: [] });
  const { handleSubmit, formState, errorState, setErrors, isDirty, setIsDirty, setFormData, onFormValueChanged, onValueTextPairChanged }
    = UseForm(INITIAL_FORM_STATE, submitFormCallback, validate);

  const onSaveClicked = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    setIsDirty(true);
    handleSubmit();
  }

  const onBackClicked = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    if (omeMeetState.route === '') {
      //OME Route
      navigate(EntryNavLinks.OU_ADMIN_ROSTER_ENTRIES_INDIVIDUAL_SELECTION, { state: { athlete: state.athlete, tryGet: false } });
    }
    else {
      //Meet Route
      navigate(NavLinks.OU_ADMIN_ROSTER_ENTRIES_INDIVIDUAL_SELECTION, { state: { athlete: state.athlete, tryGet: false } });
    }
  }

  function submitFormCallback(formState) {
    //validate that swim time meets any time cuts
    let errors = {};
    const timeCut = eventMeetEventState.arrayData?.find(x => x.eventId === formState.eventId);
    if (timeCut) {
      const swimTimeTimeFormatted = formatTimeForFilterObject(formState.swimTime);
      const swimTimeTimeInMilliseconds = convertTimeToMilliseconds(swimTimeTimeFormatted);
      const noFasterThanTimeCutFormatted = timeCut?.noFasterThanTimeCut ? formatTimeForFilterObject(formatTimeForDisplay(timeCut?.noFasterThanTimeCut)) : null;
      const noFasterThanTimeCutInMilliseconds = noFasterThanTimeCutFormatted ? convertTimeToMilliseconds(noFasterThanTimeCutFormatted) : null;
      if (noFasterThanTimeCutInMilliseconds !== null && noFasterThanTimeCutInMilliseconds >= 0) {
        if (swimTimeTimeInMilliseconds < noFasterThanTimeCutInMilliseconds) {
          errors.swimTime = `Swim Time cannot be faster than the ${timeCut.eventName} No Faster Than Time Standard Cut (${formatTimeForDisplay(timeCut?.noFasterThanTimeCut)})`;
        }
      }
      const noSlowerThanTimeCutFormatted = timeCut?.noSlowerThanTimeCut ? formatTimeForFilterObject(formatTimeForDisplay(timeCut?.noSlowerThanTimeCut)) : null;
      const noSlowerThanTimeCutInMilliseconds = noSlowerThanTimeCutFormatted ? convertTimeToMilliseconds(noSlowerThanTimeCutFormatted) : null;
      if (noSlowerThanTimeCutInMilliseconds !== null && noSlowerThanTimeCutInMilliseconds >= 0) {
        if (swimTimeTimeInMilliseconds > noSlowerThanTimeCutInMilliseconds) {
          errors.swimTime = `Swim Time cannot be slower than the ${timeCut.eventName} No Slower Than Time Standard Cut (${formatTimeForDisplay(timeCut?.noSlowerThanTimeCut)})`;
        }
      }
    }

    if (Object.keys(errors).length === 0) {
      const qualifyingCourseCode = eventMeetEventState.arrayData.find(x => x.eventId === formState.eventId)?.eventName?.substring(eventMeetEventState.arrayData.find(x => x.eventId === formState.eventId)?.eventName?.length - 3, eventMeetEventState.arrayData.find(x => x.eventId === formState.eventId)?.eventName?.length) !== 'ter' ?
        eventMeetEventState.arrayData.find(x => x.eventId === formState.eventId)?.eventName?.substring(eventMeetEventState.arrayData.find(x => x.eventId === formState.eventId)?.eventName?.length - 3, eventMeetEventState.arrayData.find(x => x.eventId === formState.eventId)?.eventName?.length) : 'OW';

      const index = orgUnitAthleteEntryIndividualRosterEntryState.arrayData?.events?.findIndex(x => x.meetEventId === state.event?.meetEventId);
      orgUnitAthleteEntryIndividualRosterEntryState.arrayData.events[index] = {
        ...state.event,
        entryMeetName: formState.meetName.trim(),
        qualifyingEventId: formState.eventId,
        qualifyingEventName: formState.eventName,
        qualifyingEventCode: eventMeetEventState.arrayData.find(x => x.eventId === formState.eventId)?.eventCode,
        isAlternateEvent: eventMeetEventState.arrayData.find(x => x.eventId === formState.eventId)?.isAlternateEvent,
        isNonConformingTime: eventMeetEventState.arrayData.find(x => x.eventId === formState.eventId)?.isNonConforming,
        qualifyingCourseId: eventMeetEventState.arrayData.find(x => x.eventId === formState.eventId)?.courseId,
        qualifyingCourseCode: qualifyingCourseCode,
        hasQualifyingTime: false,
        swimDate: formState.swimDate,
        swimTimeEntryOverride: true,
        entryTime: formState.swimTimeValue,
        isSelected: !state.event.individualAthleteMeetEventId ? false : formState.isEdit === true ? true : false
      };
      if (formState.isEdit === true && state.event?.individualAthleteMeetEventId) {
        putOrgUnitAthleteEntryIndividualRosterEntry(state.athlete?.orgUnitAthleteEntryId, [orgUnitAthleteEntryIndividualRosterEntryState.arrayData.events[index]]);
        setState({ ...state, tryGet: true, tryGetLocationState: true });
      }
      else {
        setState({ ...state, tryRedirect: true, tryGetLocationState: false });
      }
    }
    else {
      setErrors({ ...errorState, swimTime: errors.swimTime });
    }
  }

  useEffect(() => {
    if (Object.keys(omeMeetState.objData).length > 0) {
      const isSaved = formStateHasBeenSaved();
      setLeftNavModalState({ ...leftNavModalState, formStateSaved: isSaved });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [omeMeetState.objData, isDirty]);

  useEffect(() => {
    if (omeMeetOrgUnitEntryInfoState.isObjLoaded === true && location.state.event &&
      eventMeetEventState.isArrayLoading === false && eventMeetEventState.meetEventId === location.state.event?.meetEventId) {
      //Edit
      if (location.state.event?.swimTimeEntryOverride === true) {
        setFormData({
          ...formState,
          isEdit: location.state.event?.entryTime ? true : false,
          timeCuts: location.state.event?.timeCuts || [],
          swimTime: location.state.event?.entryTime ? formatTimeForDisplay(location.state.event?.entryTime) : '',
          swimTimeValue: location.state.event?.entryTime ? location.state.event?.entryTime : '',
          swimDate: location.state.event?.swimDate ? formatDate(location.state.event?.swimDate) : Constants.BLANK_DATE_STRING,
          eventId: location.state.event?.qualifyingEventId ? location.state.event?.qualifyingEventId : Constants.DEFAULT_ID,
          eventName: location.state.event?.qualifyingEventName ? location.state.event?.qualifyingEventName : '',
          courseId: location.state.event?.qualifyingCourseId ? location.state.event?.qualifyingCourseId : Constants.DEFAULT_ID,
          courseName: location.state.event?.qualifyingCourseCode ? location.state.event?.qualifyingCourseCode : '',
          meetName: location.state.event?.entryMeetName ? location.state.event?.entryMeetName : '',
          hasTimeStandards: omeMeetOrgUnitEntryInfoState.objData?.hasTimeStandards ?? false,
          hasQualifyingWindow: omeMeetOrgUnitEntryInfoState.objData?.hasQualifyingWindow ?? false,
          qualifyingStartDate: omeMeetOrgUnitEntryInfoState.objData?.timeStandardsQualifyingStartDate ? formatDate(omeMeetOrgUnitEntryInfoState.objData?.timeStandardsQualifyingStartDate) : Constants.BLANK_DATE_STRING,
          qualifyingEndDate: omeMeetOrgUnitEntryInfoState.objData?.timeStandardsQualifyingEndDate ? formatDate(omeMeetOrgUnitEntryInfoState.objData?.timeStandardsQualifyingEndDate) : Constants.BLANK_DATE_STRING
        }, true);
      } else {
        //Add
        setFormData({
          ...formState,
          isEdit: location.state.event?.swimTimeEntryOverride ? true : false,
          timeCuts: location.state.event?.timeCuts || [],
          hasTimeStandards: omeMeetOrgUnitEntryInfoState.objData?.hasTimeStandards ?? false,
          hasQualifyingWindow: omeMeetOrgUnitEntryInfoState.objData?.hasQualifyingWindow ?? false,
          qualifyingStartDate: omeMeetOrgUnitEntryInfoState.objData?.timeStandardsQualifyingStartDate ? formatDate(omeMeetOrgUnitEntryInfoState.objData?.timeStandardsQualifyingStartDate) : Constants.BLANK_DATE_STRING,
          qualifyingEndDate: omeMeetOrgUnitEntryInfoState.objData?.timeStandardsQualifyingEndDate ? formatDate(omeMeetOrgUnitEntryInfoState.objData?.timeStandardsQualifyingEndDate) : Constants.BLANK_DATE_STRING
        }, true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [omeMeetOrgUnitEntryInfoState, eventMeetEventState])

  useEffect(() => {
    if (location.state && location.state.athlete && location.state.event) {
      const omeMeetCopy = JSON.parse(JSON.stringify(omeMeetState.objData));
      omeMeetCopy.omeMeetEntryCourse.unshift({ courseId: Constants.DEFAULT_ID, course: { courseCode: '--' } });
      const courseOptions = omeMeetCopy.omeMeetEntryCourse.map((o) => { return { id: o.courseId, name: o.course?.courseCode } })
      setState({ ...state, athlete: location.state.athlete, event: location.state.event, courseOptions: courseOptions });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (state.tryGet === true && orgUnitAthleteEntryIndividualRosterEntryState.isSaving === false) {
      getOrgUnitAthleteEntryIndividualRosterEntryByOmeMeetOrgUnitEntryId(omeMeetOrgUnitEntryState?.objData.omeMeetOrgUnitEntryId);
      getOrgUnitAthleteEntryRoster(omeMeetOrgUnitEntryState?.objData.omeMeetOrgUnitEntryId);
      setState({ ...state, tryGet: false, tryRedirect: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.tryGet, orgUnitAthleteEntryIndividualRosterEntryState])

  useEffect(() => {
    if (state.tryRedirect === true && omeMeetOrgUnitAthleteEntryIndividualRosterEntryState.isArrayLoading === false
      && omeMeetOrgUnitAthleteEntryState.isArrayLoading === false) {
      const selectedAthlete = omeMeetOrgUnitAthleteEntryIndividualRosterEntryState.arrayData.find(x => x.personId === state.athlete.personId)
      const selectedEvents = selectedAthlete.events;
      const updatedAthlete = { ...state.athlete, events: selectedEvents };
      if (omeMeetState.route === '') {
        //OME Route
        navigate(EntryNavLinks.OU_ADMIN_ROSTER_ENTRIES_INDIVIDUAL_SELECTION, { state: { athlete: updatedAthlete, tryGet: state.tryGetLocationState === true ? true : false } });
      }
      else {
        //Meet Route
        navigate(NavLinks.OU_ADMIN_ROSTER_ENTRIES_INDIVIDUAL_SELECTION, { state: { athlete: updatedAthlete, tryGet: state.tryGetLocationState === true ? true : false } });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.tryRedirect, omeMeetOrgUnitAthleteEntryIndividualRosterEntryState, omeMeetOrgUnitAthleteEntryState])

  function formStateHasBeenSaved() {
    //see if formState has been saved
    if (isDirty === true) {
      return false;
    }

    return true;
  }

  return {
    eventMeetEventState,
    state,
    orgUnitAthleteEntryIndividualRosterEntryState,
    omeMeetOrgUnitAthleteEntryIndividualRosterEntryState,
    omeMeetOrgUnitAthleteEntryState,
    onSaveClicked,
    onBackClicked,
    onFormValueChanged,
    onValueTextPairChanged,
    formState,
    errorState
  };
}

export default useOrgUnitAdminRosterEntriesIndividualOverride;